/**
 *
 * @module userActivity
 * @desc userActivity sequence implementation
 * @see https://github.bamtech.co/sdk-distribution/bam-sdk/blob/master/Features/UserActivityApi.md
 * @see https://github.bamtech.co/fed-core/browser-sdk/blob/master/docs/userActivity/UserActivityApi.md
 *
 */

import Check from 'check-types';
import assert from 'assert';

import SDK from './../../sdk';
import BaseSequence from './../base';
import Exception from './../../services/exception/entry';

const InvalidArgumentException = Exception.InvalidArgumentException;

const {
    boolean,
    nonEmptyObject
} = Check.assert;

/**
 *
 * @class UserActivitySequence
 * @extends {BaseSequence}
 *
 * @example const userActivitySequence = new SDK.Test.Sequences.UserActivity(config);
 *
 */
export default class UserActivitySequence extends BaseSequence {

    /**
     *
     * @param {Object} config configuration used to build a BootstrapConfiguration instance
     * @param {Boolean} [clearOnComplete=false] flag that determines if token refresh
     * should be cleared when the sequence is complete (useful when running tests)
     * @throws {InvalidArgumentException}
     *
     */
    constructor(config, clearOnComplete = false) {

        const errorMessage = {
            ctor: 'UserActivitySequence(config, clearOnComplete)',
            get config() {
                return `${this.ctor} config should be a non-empty Object`;
            },
            get clearOnComplete() {
                return `${this.ctor} clearOnComplete should be a Boolean`;
            }
        };

        nonEmptyObject(config, errorMessage.config, InvalidArgumentException);
        boolean(clearOnComplete, errorMessage.clearOnComplete, InvalidArgumentException);

        super(config);

        /**
         *
         * @access private
         * @type {Boolean}
         *
         */
        this.clearOnComplete = clearOnComplete;

    }

    /**
     *
     * @access public
     * @method execute
     * @desc runs the sequence to completion
     *
     * @example userActivitySequence.execute().then(() => { console.log('success'); });
     *
     */
    execute() {

        const { config, clearOnComplete } = this;
        const { clientId, clientApiKey, environment, debugEnabled } = config;

        const BootstrapConfiguration = SDK.Services.Configuration.BootstrapConfiguration;
        const DustGlimpseReference = SDK.Services.Internal.Dust.DustUrnReference.userActivity.glimpse;
        const UserActivityAction = SDK.UserActivity.Personalization.UserActivityAction;
        const GlimpseEvents = SDK.UserActivity.Glimpse.GlimpseEvents;
        const EventSchema = SDK.UserActivity.EventSchema;
        const EventValue = SDK.UserActivity.EventValue;

        return new Promise((resolve, reject) => {

            /** @type {SDK.Services.Configuration.BootstrapConfiguration} */
            const bootstrapConfiguration = new BootstrapConfiguration({
                clientId,
                clientApiKey,
                environment,
                debugEnabled
            });

            /** @type {SDK.SdkSession} */
            let sdkSession;

            /** @type {SDK.UserActivity.UserActivityApi} */
            let userActivityApi;

            const globalCountryCode = 'US';
            const overriddenGlobalCountryCode = 'NL';
            const appLaunchedUrn = DustGlimpseReference.appLaunched;
            const contentSelectedUrn = DustGlimpseReference.contentSelected;
            const mashupParameter1 = 'ABCD';
            const mashupParameter2 = '1234';
            const rewardToken = 'E8C9A38F-C773-4E48-B3E8-187375736854';
            const rewardContentId = '324DDA5D-DA1A-4CA3-9C1B-4EE9D0E3DE6D';
            const action = UserActivityAction.CLICK;
            const recommendedContentIds = [
                '69E58F96-9355-4B16-B77E-AFB54E9EDACB',
                '3877E6EE-99EE-49D6-A82F-F381883E5DB7'
            ];

            const trackingParameters = {
                globalCountryCode
            };

            SDK.SdkSession.createSdkSession(bootstrapConfiguration).then((_sdkSession) => {

                /** @type {SDK.SdkSession} sa */
                sdkSession = _sdkSession;

                /** @test */
                assert.ok(_sdkSession);

                /** Establish Anonymous Session */
                return sdkSession.initialize();

            }).then(() => {

                /** @type {SDK.UserActivity.UserActivityApi} */
                userActivityApi = sdkSession.userActivityApi;

                userActivityApi.setTrackingParameters(trackingParameters);

                /** @test */
                assert.deepEqual(userActivityApi.trackingParameters, trackingParameters);

                /** @type {Array<SDK.UserActivity.EventSchema>} */
                const schemaArray = [
                    new EventSchema({
                        appLaunched: new EventValue(appLaunchedUrn)
                    })
                ];

                /** @type {SDK.UserActivity.Glimpse.GlimpseEvent} */
                const event = GlimpseEvents.appLaunched;

                /** @type {Object} */
                const schemataOptions = {
                    event,
                    schemata: schemaArray,
                    passthrough: true // include all tracking parameters with the payload
                };

                userActivityApi.schemataProvider.setSchemata(schemataOptions);

                return userActivityApi.trackEvent(event, {});

            }).then((payload) => {

                /** @test verify that the payload is correct given the schema inputs and passthrough value */
                assert.equal(payload.globalCountryCode, trackingParameters.globalCountryCode);
                assert.equal(payload.appLaunched, appLaunchedUrn);

                /** @type {Array<SDK.UserActivity.EventSchema>} */
                const schemaArray = [
                    new EventSchema({
                        contentSelected: new EventValue(contentSelectedUrn)
                    })
                ];

                /** @type {SDK.UserActivity.Glimpse.GlimpseEvent} */
                const event = GlimpseEvents.contentSelected;

                /** @type {Object} */
                const schemataOptions = {
                    event,
                    schemata: schemaArray,
                    passthrough: false // exclude all tracking parameters from the payload
                };

                userActivityApi.schemataProvider.setSchemata(schemataOptions);

                const additionalParameters = {
                    additionalTrackingParameter: mashupParameter1
                };

                return userActivityApi.trackEvent(event, additionalParameters);

            }).then((payload) => {

                /** @test verify that the payload is correct given the schema inputs and passthrough value */
                assert.equal(payload.contentSelected, contentSelectedUrn);
                assert.equal(payload.globalCountryCode, undefined);
                assert.equal(payload.additionalTrackingParameter, undefined);

                /** @type {Array<SDK.UserActivity.EventSchema>} */
                const schemaArray = [
                    new EventSchema({
                        purchaseCompleted: new EventValue(() => this.createFailure())
                    })
                ];

                /** @type {SDK.UserActivity.Glimpse.GlimpseEvent} */
                const event = GlimpseEvents.purchaseCompleted;

                /** @type {Object} */
                const schemataOptions = {
                    event,
                    schemata: schemaArray,
                    passthrough: false // exclude all tracking parameters from the payload
                };

                userActivityApi.schemataProvider.setSchemata(schemataOptions);

                return userActivityApi.trackEvent(event, {});

            }).then((payload) => {

                /** @test verify that the payload is correct given the schema inputs and passthrough value */
                assert.equal(payload._failures.length, 1);
                assert.equal(payload.purchaseCompleted, undefined);
                assert.equal(payload.globalCountryCode, undefined);

                /** @type {Array<SDK.UserActivity.EventSchema>} */
                const schemaArray = [
                    new EventSchema({
                        appLaunched: new EventValue(appLaunchedUrn)
                    })
                ];

                /** @type {SDK.UserActivity.Glimpse.GlimpseEvent} */
                const event = GlimpseEvents.appLaunched;

                /** @type {Object} */
                const schemataOptions = {
                    event,
                    schemata: schemaArray,
                    passthrough: true // include all tracking parameters with the payload
                };

                userActivityApi.schemataProvider.setSchemata(schemataOptions);

                /** @type {Object} */
                const overriddenParameters = {
                    globalCountryCode: overriddenGlobalCountryCode
                };

                return userActivityApi.trackEvent(event, overriddenParameters);

            }).then((payload) => {

                /** @test verify that the payload is correct given the schema inputs and passthrough value */
                assert.equal(payload.globalCountryCode, overriddenGlobalCountryCode);
                assert.equal(payload.appLaunched, appLaunchedUrn);

                return userActivityApi.sendContentReward(
                    rewardToken,
                    rewardContentId,
                    action,
                    recommendedContentIds
                );

            }).then((payload) => {

                /** @test verify that the payload is correct given the sendContentReward inputs */
                assert.equal(payload.rewardToken, rewardToken);
                assert.equal(payload.rewardContentId, rewardContentId);
                assert.equal(payload.action, action);
                assert.equal(payload.recommendedContentIds, recommendedContentIds);

                /** @type {Array<SDK.UserActivity.EventSchema>} */
                const schemaArray = [
                    new EventSchema({
                        appLaunched: new EventValue(appLaunchedUrn)
                    }),
                    new EventSchema({
                        appLaunched: new EventValue(contentSelectedUrn)
                    }),
                    new EventSchema({
                        contentSelected: new EventValue(contentSelectedUrn)
                    })
                ];

                /** @type {SDK.UserActivity.Glimpse.GlimpseEvent} */
                const event = GlimpseEvents.appLaunched;

                /** @type {Object} */
                const schemataOptions = {
                    event,
                    schemata: schemaArray,
                    passthrough: true // include all tracking parameters with the payload
                };

                userActivityApi.schemataProvider.setSchemata(schemataOptions);

                return userActivityApi.trackEvent(event, {});

            }).then((payload) => {

                /** @test verify that the payload is correct given the schema inputs and passthrough value */
                assert.equal(payload.globalCountryCode, trackingParameters.globalCountryCode);
                assert.equal(payload.appLaunched, contentSelectedUrn);
                assert.equal(payload.contentSelected, contentSelectedUrn);

                /** @type {Array<SDK.UserActivity.EventSchema>} */
                const schemaArray = [
                    new EventSchema({
                        purchaseCompleted: new EventValue((payloadParams) => {
                            return payloadParams.mashupParameter1 + payloadParams.mashupParameter2;
                        })
                    })
                ];

                /** @type {SDK.UserActivity.Glimpse.GlimpseEvent} */
                const event = GlimpseEvents.purchaseCompleted;

                /** @type {Object} */
                const schemataOptions = {
                    event,
                    schemata: schemaArray,
                    passthrough: false // exclude global trackingParameters from the payload
                };

                userActivityApi.schemataProvider.setSchemata(schemataOptions);

                /** @type {Object} */
                const extraParameters = {
                    mashupParameter1,
                    mashupParameter2
                };

                return userActivityApi.trackEvent(event, extraParameters);

            }).then((payload) => {

                /** @test verify that the payload is correct given the schema inputs and passthrough value */
                assert.equal(payload.globalCountryCode, undefined);
                assert.equal(payload.purchaseCompleted, mashupParameter1 + mashupParameter2);

                if (clearOnComplete) {
                    if (sdkSession.tokenManager) {
                        sdkSession.tokenManager.clearRetryRefresh();
                    }
                }

                return resolve();

            }).catch((ex) => {

                return reject(ex);

            });

        });

    }

}



// WEBPACK FOOTER //
// ./src/sequences/userActivity/userActivity.js